import React, { useEffect, useState } from "react";
import { Alert, Text, View } from "react-native";
import { FilePlus, PackagePlus, UserPlus, Files, Users, Boxes, BarChart3, Receipt, CreditCard } from "lucide-react-native";
import { api } from "../api/client";
import { formatCurrency } from "../utils/format";
import { Badge, Button, Card, DataTable, ScreenHeader, StatCard, Toolbar } from "../components/UI";
import { colors, styles } from "../styles";

export function DashboardScreen({ navigate }) {
  const [data, setData] = useState({ recent_quotations: [] });

  useEffect(() => {
    api.dashboard().then(setData).catch((error) => Alert.alert("Dashboard", error.message));
  }, []);

  return (
    <View style={styles.page}>
      <ScreenHeader title="Dashboard" subtitle="Company quotation & billing activity at a glance" />
      
      <View style={styles.row}>
        <StatCard label="Total Clients" value={data.total_clients || 0} icon={Users} subtitle="Active clients in database" />
        <StatCard label="Products / Items" value={data.total_products || 0} icon={Boxes} subtitle="Catalog product master data" />
        <StatCard label="Total Quotations" value={data.total_quotations || 0} icon={BarChart3} subtitle={`${data.approved_quotations || 0} approved quotations`} />
        <StatCard label="Total Invoices" value={data.total_invoices || 0} icon={Receipt} subtitle={`${data.paid_invoices || 0} paid, ${data.issued_invoices || 0} issued`} />
      </View>

      <View style={styles.row}>
        <StatCard label="Total Invoiced" value={formatCurrency(data.total_invoiced_amount || 0)} icon={Receipt} subtitle="Billed to clients" />
        <StatCard label="Total Collected" value={formatCurrency(data.total_received_amount || 0)} icon={CreditCard} subtitle="Payments received" />
        <StatCard label="Outstanding Balance" value={formatCurrency(data.total_outstanding_amount || 0)} icon={BarChart3} subtitle="Receivable pending collection" />
      </View>

      <Card style={{ padding: 18, gap: 12 }}>
        <Text style={[styles.h2, { fontSize: 18 }]}>Quick Actions</Text>
        <Toolbar style={{ padding: 12, gap: 10, flexWrap: "wrap" }}>
          <Button icon={FilePlus} onPress={() => navigate("newQuotation")} style={{ height: 42, paddingHorizontal: 14 }}>New Quotation</Button>
          <Button icon={Receipt} variant="secondary" onPress={() => navigate("invoice")} style={{ height: 42, paddingHorizontal: 14 }}>Invoices</Button>
          <Button icon={UserPlus} variant="secondary" onPress={() => navigate("clients")} style={{ height: 42, paddingHorizontal: 14 }}>Add Client</Button>
          <Button icon={PackagePlus} variant="secondary" onPress={() => navigate("products")} style={{ height: 42, paddingHorizontal: 14 }}>Add Product</Button>
          <Button icon={Files} variant="secondary" onPress={() => navigate("quotations")} style={{ height: 42, paddingHorizontal: 14 }}>View All Quotations</Button>
        </Toolbar>
      </Card>

      <Card style={{ padding: 18, gap: 12 }}>
        <View style={styles.between}>
          <Text style={[styles.h2, { fontSize: 18 }]}>Recent Quotations</Text>
          <Button variant="secondary" onPress={() => navigate("quotations")} style={{ height: 36, paddingHorizontal: 12 }}>View All</Button>
        </View>
        <DataTable
          columns={[
            { key: "quotation_number", label: "Quotation No", style: { fontWeight: "700" } },
            { key: "client_name", label: "Client Name" },
            { key: "project_title", label: "Project Title" },
            {
              key: "grand_total",
              label: "Grand Total (PKR)",
              render: (r) => `PKR ${Number(r.grand_total || 0).toLocaleString("en-PK", { minimumFractionDigits: 2 })}`
            },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <Badge variant={r.status === "approved" ? "success" : r.status === "rejected" ? "danger" : "pending"}>
                  {String(r.status || "draft").toUpperCase()}
                </Badge>
              )
            }
          ]}
          rows={data.recent_quotations || []}
        />
      </Card>
    </View>
  );
}
